/*
  Knit-structure colour swatches.

  Paints the product's knit structure over its colour swatches (cards, PDP colour option, sibling colours).
  A product opts in through the attributes printed by snippets/knit-swatch-attrs.liquid from its metafields:
    data-knit="jersey|rib|cable|waffle|links|pointelle|woven"   (custom.knit_structure)
    data-knit-gauge="<needles per inch>"                         (custom.knit_gauge)
  Swatches outside such an element (facets, products without the metafield) stay flat colour.

  The tiles are SVGs drawn per (structure, colour, gauge), ported from the owner's Claude Design "Knit Swatches"
  (2026-10-22): stitch tones are mixed from the swatch's own colour, stitch size scales with 6 / gauge, and a
  milled-cashmere finish (slight blur, softer contrast, soft-light fibre noise, inset shade) sits on top.
  The layers are decorative (aria-hidden); the swatch keeps its own accessible name.
*/
(() => {
  const SWATCHES = [
    '.product-option__swatch:not(.product-option__swatch--unavailable)',
    '.product-option__swatch-color',
    '.product-card__color-sibling__swatch',
  ].join(',');

  // milled cashmere finish
  const FIBRE = { blur: 0.65, con: 0.78, sat: 0.94, bri: 1.03, fuzz: 0.55 };

  const rgb = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  const hex = (a) => '#' + a.map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('');
  const mix = (a, b, t) => {
    const x = rgb(a);
    const y = rgb(b);
    return hex(x.map((v, i) => v + (y[i] - v) * t));
  };
  const lum = (c) => {
    const [r, g, b] = rgb(c);
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  };

  const gScale = (gg) => 6 / gg;
  const gDepth = (gg) => Math.min(1.3, 0.7 + (6 / gg - 0.5) * 0.3);

  function tones(c, gg) {
    const L = lum(c);
    const lt = L > 0.75;
    const dk = L < 0.12;
    const k = gDepth(gg);
    const m = (v) => Math.min(0.92, v * k);
    return {
      ply: gg <= 3.5,
      b: dk ? mix(c, '#ffffff', 0.08) : c,
      d: mix(c, '#000000', m(lt ? 0.12 : dk ? 0.45 : 0.3)),
      dd: mix(c, '#000000', m(lt ? 0.24 : dk ? 0.75 : 0.55)),
      h: mix(c, '#ffffff', dk ? 0.2 : lt ? 0.6 : 0.24),
      hole: L < 0.35 ? mix(c, '#fbfaf8', 0.7) : mix(c, '#000000', lt ? 0.45 : 0.6),
    };
  }

  const svg = (w, h, body) =>
    `url("data:image/svg+xml,${encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">${body}</svg>`
    )}")`;

  const FUZZ = `url("data:image/svg+xml,${encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120"><filter id="n" x="0" y="0"><feTurbulence type="fractalNoise" baseFrequency="1.1" numOctaves="2" stitchTiles="stitch"/><feColorMatrix values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  1.6 0 0 0 -0.55"/></filter><rect width="120" height="120" filter="url(#n)"/></svg>'
  )}")`;

  // one loop leg of a knit stitch; chunky gauges show the plies
  const leg = (cx, cy, r, t) =>
    `<ellipse cx="${cx}" cy="${cy}" rx="2.25" ry="5.4" transform="rotate(${r} ${cx} ${cy})" fill="${t.b}" stroke="${t.d}" stroke-width="0.5"/>` +
    `<ellipse cx="${cx - 0.5}" cy="${cy - 0.8}" rx="0.65" ry="3.2" transform="rotate(${r} ${cx} ${cy})" fill="${t.h}" opacity="0.75"/>` +
    (t.ply
      ? `<g transform="rotate(${r} ${cx} ${cy})" stroke="${t.d}" stroke-width="0.35" opacity="0.8"><path d="M${cx - 2} ${cy - 2.6} L${cx + 2} ${cy - 3.8}"/><path d="M${cx - 2.2} ${cy + 0.6} L${cx + 2.2} ${cy - 0.6}"/><path d="M${cx - 2} ${cy + 3.8} L${cx + 2} ${cy + 2.6}"/></g>`
      : '');
  const stitch = (x, y, t) => leg(x + 2.8, y + 4.5, -24, t) + leg(x + 7.2, y + 4.5, 24, t);

  const STRUCT = {
    jersey: {
      w: 10,
      h: 9,
      draw: (t) => {
        let s = `<rect width="10" height="9" fill="${t.dd}"/>`;
        for (const y of [18, 9, 0, -9]) s += stitch(0, y, t);
        return s;
      },
    },
    rib: {
      w: 14,
      h: 9,
      draw: (t) => {
        let s = `<rect width="14" height="9" fill="${t.dd}"/><ellipse cx="12" cy="2.25" rx="1.5" ry="1" fill="${t.d}"/><ellipse cx="12" cy="6.75" rx="1.5" ry="1" fill="${t.d}"/>`;
        for (const y of [18, 9, 0, -9]) s += stitch(0, y, t);
        return s + `<rect x="9.6" width="0.8" height="9" fill="${t.dd}" opacity="0.6"/>`;
      },
    },
    cable: {
      w: 32,
      h: 32,
      draw: (t) => {
        let s = `<rect width="32" height="32" fill="${t.d}"/>`;
        for (let r = 0; r < 8; r++) {
          for (let c = -1; c < 9; c++) {
            s += `<ellipse cx="${c * 4 + (r % 2 ? 2 : 0)}" cy="${r * 4 + 2}" rx="1.8" ry="1.15" fill="${t.b}" opacity="0.6"/>`;
          }
        }
        s += `<rect x="6.5" width="19" height="32" fill="${t.dd}"/>`;
        const strand = (p) =>
          `<path d="${p}" fill="none" stroke="${t.dd}" stroke-width="8.6"/><path d="${p}" fill="none" stroke="${t.b}" stroke-width="7"/>` +
          `<path d="${p}" fill="none" stroke="${t.d}" stroke-width="0.8"/><path d="${p}" fill="none" stroke="${t.h}" stroke-width="1.4" stroke-dasharray="2 2" transform="translate(-1.8 0)" opacity="0.7"/>`;
        return s + strand('M20 -4 L20 0 C20 9 12 9 12 18 L12 36') + strand('M12 -4 L12 0 C12 9 20 9 20 18 L20 36');
      },
    },
    waffle: {
      w: 12,
      h: 12,
      draw: (t) =>
        `<rect width="12" height="12" fill="${t.dd}"/><rect x="1.6" y="1.6" width="8.8" height="8.8" rx="1.4" fill="${t.b}"/>` +
        `<path d="M2.4 9.6 V2.4 H9.6" fill="none" stroke="${t.h}" stroke-width="0.9" opacity="0.8"/><path d="M2.4 9.8 H9.8 V2.4" fill="none" stroke="${t.d}" stroke-width="0.9"/>`,
    },
    links: {
      w: 8,
      h: 8,
      draw: (t) => {
        const b = (x, y) =>
          `<ellipse cx="${x}" cy="${y}" rx="3.5" ry="1.75" fill="${t.b}" stroke="${t.d}" stroke-width="0.4"/><ellipse cx="${x}" cy="${y - 0.6}" rx="2" ry="0.6" fill="${t.h}" opacity="0.6"/>`;
        return `<rect width="8" height="8" fill="${t.dd}"/>` + b(4, 2) + b(0, 6) + b(8, 6);
      },
    },
    pointelle: {
      w: 20,
      h: 36,
      draw: (t) => {
        let s = `<rect width="20" height="36" fill="${t.dd}"/>`;
        for (const y of [45, 36, 27, 18, 9, 0, -9]) {
          for (const x of [0, 10]) {
            if (!((x === 0 && (y === 0 || y === 36)) || (x === 10 && y === 18))) s += stitch(x, y, t);
          }
        }
        const eye = (x, y) =>
          `<circle cx="${x}" cy="${y}" r="4.3" fill="${t.b}" stroke="${t.dd}" stroke-width="0.6"/><circle cx="${x}" cy="${y}" r="3" fill="${t.hole}"/>` +
          `<path d="M${x - 3} ${y - 0.3} A3 3 0 0 1 ${x + 3} ${y - 0.3}" fill="none" stroke="${t.dd}" stroke-width="0.6"/>`;
        return s + eye(5, 4.5) + eye(5, 40.5) + eye(15, 22.5);
      },
    },
    woven: {
      w: 8,
      h: 8,
      draw: (t) => {
        const l = (p, c, w, o) => `<path d="${p}" stroke="${c}" stroke-width="${w}" opacity="${o}"/>`;
        const P = ['M-2 10 L10 -2', 'M-2 2 L2 -2', 'M6 10 L10 6'];
        return (
          `<rect width="8" height="8" fill="${t.d}"/>` +
          P.map((p) => l(p, t.b, 3.2, 1)).join('') +
          P.map((p) => `<g transform="translate(-0.9 -0.9)">${l(p, t.h, 0.7, 0.6)}</g>`).join('')
        );
      },
    },
  };

  const cache = new Map();
  const tile = (k, c, gg) => {
    const key = `${k}|${c}|${gg}`;
    if (!cache.has(key)) cache.set(key, svg(STRUCT[k].w, STRUCT[k].h, STRUCT[k].draw(tones(c, gg))));
    return cache.get(key);
  };

  const cssHex = (value) => {
    const m = value && value.match(/rgba?\(([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:[,\s/]+([\d.]+))?/);
    if (!m || (m[4] !== undefined && parseFloat(m[4]) === 0)) return null;
    return hex([m[1], m[2], m[3]].map(Number));
  };

  const STYLE = `
.knit-swatch{overflow:hidden;isolation:isolate}
.knit-swatch>.knit-swatch__layer{position:absolute;pointer-events:none;border-radius:inherit}
.knit-swatch>.knit-swatch__tex{inset:-3px;background-image:var(--knit-bg);background-size:var(--knit-size);background-position:3px 3px;filter:var(--knit-fx)}
.knit-swatch>.knit-swatch__fuzz{inset:0;background-image:var(--knit-fuzz);background-size:var(--knit-fuzz-size);mix-blend-mode:soft-light;opacity:${FIBRE.fuzz}}
.knit-swatch>.knit-swatch__shade{inset:0;box-shadow:inset 0 1px 2px rgba(0,0,0,.28),inset 0 -1px 1px rgba(255,255,255,.18)}`;

  function paint(el, k, gg) {
    if (el.dataset.knitPainted) return; // re-rendered swatches are new elements
    const cs = getComputedStyle(el);
    const c = cssHex(cs.backgroundColor);
    if (!c || cs.backgroundImage.startsWith('url(')) return; // no colour, or an image swatch
    const key = `${k}|${c}|${gg}`;

    // stitch size: the design's 84px swatch at 6/gauge, its 34px swatch at 0.62 of that. Our swatches are 20-32px, so
    // the gauge step is softened (^0.7) and floored at 0.6 to keep fine-gauge stitches visible: 12gg still reads finer
    // than 7gg, 3.5gg chunkier.
    const d = el.getBoundingClientRect().width || 24;
    const s = gScale(gg) ** 0.7 * Math.max(0.6, Math.min(1, 0.25 + d * 0.011));
    const blur = d > 40 ? 1 : 0.55;
    el.style.setProperty('--knit-bg', tile(k, c, gg));
    el.style.setProperty('--knit-size', `${STRUCT[k].w * s}px ${STRUCT[k].h * s}px`);
    el.style.setProperty('--knit-fx', `blur(${(blur * FIBRE.blur).toFixed(2)}px) contrast(${FIBRE.con}) saturate(${FIBRE.sat}) brightness(${FIBRE.bri})`);
    el.style.setProperty('--knit-fuzz', FUZZ);
    el.style.setProperty('--knit-fuzz-size', d > 40 ? '90px 90px' : '60px 60px');
    if (!el.querySelector(':scope > .knit-swatch__tex')) {
      for (const layer of ['tex', 'fuzz', 'shade']) {
        const span = document.createElement('span');
        span.className = `knit-swatch__layer knit-swatch__${layer}`;
        span.setAttribute('aria-hidden', 'true');
        el.appendChild(span);
      }
    }
    // the layers are absolute: anchor them on the swatch (card sibling dots are position: static)
    if (cs.position === 'static') el.style.position = 'relative';
    el.classList.add('knit-swatch');
    el.dataset.knitPainted = key;
  }

  function scan(root = document) {
    root.querySelectorAll('[data-knit]').forEach((scope) => {
      const k = scope.dataset.knit;
      if (!STRUCT[k]) return;
      const gg = Math.min(12, Math.max(1.5, parseFloat(scope.dataset.knitGauge) || 7));
      scope.querySelectorAll(SWATCHES).forEach((el) => {
        if (el.closest('[data-knit]') === scope) paint(el, k, gg);
      });
    });
  }

  function init() {
    const style = document.createElement('style');
    style.id = 'knit-swatch-css';
    style.textContent = STYLE;
    document.head.appendChild(style);
    scan();

    // variant changes, collection filtering and quick views re-render swatches
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        scan();
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
